import React from "react";
import { Download, Eye, FileText } from "lucide-react";

interface FileItem {
  id: number;
  name: string;
  url: string;
  description?: string;
  uploadedAt?: string;
  size?: number;
}

interface FileListProps {
  files: FileItem[];
  title?: string;
  emptyMessage?: string;
}

/**
 * Reusable file list component
 * Displays a list of files with download and view options
 */
const FileList: React.FC<FileListProps> = ({
  files,
  emptyMessage = "파일이 없습니다.",
}) => {
  const formatFileSize = (bytes: number): string => {
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ko-KR", { year: "numeric", month: "long" });
  };

  if (files.length === 0) {
    return (
      <div className="text-center py-8">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {files.map((file) => (
        <div
          key={file.id}
          className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              <div>
                <h4 className="font-semibold text-gray-900">{file.name}</h4>
                {file.description && (
                  <p className="text-sm text-gray-600">{file.description}</p>
                )}
                {(file.uploadedAt || file.size) && (
                  <p className="text-xs text-gray-500">
                    {file.uploadedAt && `업로드일: ${formatDate(file.uploadedAt)}`}
                    {file.uploadedAt && file.size && " • "}
                    {file.size && `크기: ${formatFileSize(file.size)}`}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <a
                href={file.url}
                download
                className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                <Download className="w-4 h-4" />
                다운로드
              </a>
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                <Eye className="w-4 h-4" />
                보기
              </a>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FileList;
